import { produce, setAutoFreeze } from "immer";
import { ContextData, MultiContextData, ReportContext, SheetDiff } from "@reporting/rp-types/analytics";
import { UidMultiContextDataItem, createItemUid, ensureItemUids, findItemIndexByUid } from "./itemUid";

// In development the store snapshots are frozen, so any leftover direct
// mutation of a draft/pristine context throws immediately instead of
// silently corrupting the state. Everything that legitimately edits a
// context works on a working copy (containers, filter sections, subeditors)
// and commits through the store API. Kept off in production - a missed
// mutation site should not crash the app there.
const DEV = typeof location !== 'undefined' && /^(localhost|127\.|0\.0\.0\.0)/.test(location.hostname);
setAutoFreeze(DEV);

export type ReportChangeScope =
	/** items added/removed/duplicated/split */
	'structure' |
	/** item positions or sizes */
	'geometry' |
	/** dashboard view_options (incl. common item_style) */
	'options' |
	/** properties/style/content of a single item */
	'content' |
	/** common or item filters */
	'filters' |
	/** whole context replaced (discard, reload, save) */
	'reset';

export class ReportChangeEvent extends Event {
	constructor(readonly scope: ReportChangeScope) {
		super('change');
	}
}

/**
 * Single source of truth for one open report/dashboard context.
 *
 * Holds two immutable snapshots: the pristine (last saved) version and the
 * current draft. Every mutation goes through `update()` (an immer recipe),
 * which produces a new draft snapshot and emits a scoped `change` event.
 * Dirty state is a plain reference comparison, so nothing needs to track
 * it by hand. See REFACTORING-context-state.md, section 2.1.
 */
export class ReportStore extends EventTarget {

	private _pristine: ReportContext;
	private _context: ReportContext;

	constructor(context: ReportContext) {
		super();
		this._pristine = produce(context, draft => {
			ensureItemUids(draft.context as MultiContextData);
		});
		this._context = this._pristine;
	}

	/** current draft snapshot; a new object after every update, never mutate it directly */
	get context(): ReportContext {
		return this._context;
	}

	get contextData() {
		return this._context.context;
	}

	get idContext() {
		return this._context.id_context;
	}

	get isDirty() {
		return this._context !== this._pristine;
	}

	get items(): UidMultiContextDataItem[] {
		return (this.contextData as MultiContextData)?.items ?? [];
	}

	getItem(uid: string): UidMultiContextDataItem {
		return this.items.find(item => item.uid === uid) ?? null;
	}

	indexOf(uid: string) {
		return findItemIndexByUid(this.contextData as MultiContextData, uid);
	}

	/**
	 * Apply an immer recipe to the draft. Returns true when the recipe
	 * actually changed something (and a change event was emitted).
	 */
	update(recipe: (context: ReportContext) => void, scope: ReportChangeScope = 'content'): boolean {
		const next = produce(this._context, recipe);
		if (next === this._context) return false;
		this._context = next;
		this.dispatchEvent(new ReportChangeEvent(scope));
		return true;
	}

	/** apply a recipe to one dashboard item, resolved by its uid */
	updateItem(uid: string, recipe: (item: UidMultiContextDataItem) => void, scope: ReportChangeScope = 'content') {
		return this.update(context => {
			const items = (context.context as MultiContextData)?.items ?? [];
			const item = items.find(i => (i as UidMultiContextDataItem).uid === uid);
			if (item) recipe(item);
		}, scope);
	}

	/** apply a recipe to the dashboard view_options (created when missing) */
	updateViewOptions(recipe: (viewOptions: Record<string, any>) => void, scope: ReportChangeScope = 'options') {
		return this.update(context => {
			const contextData = context.context as MultiContextData;
			contextData.view_options ??= {};
			recipe(contextData.view_options);
		}, scope);
	}

	/** write item geometry (x/y/width/height) by uid, e.g. after drag&drop or collision fixes */
	updateItemGeometry(positions: {uid: string, x: number, y: number, width: number, height: number}[]) {
		return this.update(context => {
			const items = ((context.context as MultiContextData)?.items ?? []) as UidMultiContextDataItem[];
			positions.forEach(pos => {
				const item = items.find(i => i.uid === pos.uid);
				if (!item) return;
				item.x = pos.x;
				item.y = pos.y;
				item.width = pos.width;
				item.height = pos.height;
			});
		}, 'geometry');
	}

	/** append a new item; assigns a uid when missing and returns it */
	addItem(item: UidMultiContextDataItem): string {
		const uid = item.uid ?? createItemUid();
		this.update(context => {
			const contextData = context.context as MultiContextData;
			contextData.items ??= [];
			const newItem: UidMultiContextDataItem = {...item, uid};
			contextData.items.push(newItem);
		}, 'structure');
		return uid;
	}

	removeItem(uid: string) {
		return this.update(context => {
			const items = ((context.context as MultiContextData)?.items ?? []) as UidMultiContextDataItem[];
			const index = items.findIndex(i => i.uid === uid);
			if (index >= 0) items.splice(index, 1);
		}, 'structure');
	}

	/** insert a deep copy right after the source item, returns the uid of the duplicate */
	duplicateItem(uid: string): string {
		const newUid = createItemUid();
		const changed = this.update(context => {
			const items = ((context.context as MultiContextData)?.items ?? []) as UidMultiContextDataItem[];
			const index = items.findIndex(i => i.uid === uid);
			if (index < 0) return;
			const clone: UidMultiContextDataItem = JSON.parse(JSON.stringify(items[index]));
			clone.uid = newUid;
			items.splice(index + 1, 0, clone);
		}, 'structure');
		return changed ? newUid : null;
	}

	/** replace the item with `count` parts (og-dashboard split action) */
	splitItem(uid: string, count: number) {
		return this.update(context => {
			const items = ((context.context as MultiContextData)?.items ?? []) as UidMultiContextDataItem[];
			const index = items.findIndex(i => i.uid === uid);
			if (index < 0) return;
			const contextItem = items[index];
			items.splice(index, 1, ...Array.from({length: count}, (_, i) => ({
				source_index: index,
				source_subindex: i,
				...contextItem,
				// the first part keeps the original identity, the others are new items
				...(i == 0 ? {} : {context: null as ContextData, id_context: null as number, uid: createItemUid()})
			})));
		}, 'structure');
	}

	/** rename an item (spreadsheet sheet tab, dashboard item title) */
	renameItem(uid: string, title: string) {
		return this.updateItem(uid, item => {
			item.title = title;
		}, 'content');
	}

	/** persist the sheet modifications of a spreadsheet item: the diff of a data sheet or the full model of a free-form sheet */
	updateItemSheet(uid: string, patch: {sheet_diff?: SheetDiff | null, sheet_model?: Record<string, any>}) {
		return this.updateItem(uid, item => {
			if (patch.sheet_diff !== undefined) item.sheet_diff = patch.sheet_diff ?? undefined;
			if (patch.sheet_model !== undefined) item.sheet_model = patch.sheet_model;
		}, 'content');
	}

	/** replace the item's embedded context data (e.g. from the sidebar filter working copy) */
	updateItemContext(uid: string, contextData: ContextData, lockedDimensions?: string[]) {
		return this.updateItem(uid, item => {
			item.context = JSON.parse(JSON.stringify(contextData));
			if (lockedDimensions) item.locked_dimensions = lockedDimensions;
		}, 'filters');
	}

	/** adopt the server-returned context after a successful save; the store becomes clean */
	markSaved(saved: ReportContext) {
		this._pristine = produce(saved, draft => {
			ensureItemUids(draft.context as MultiContextData);
		});
		this._context = this._pristine;
		this.dispatchEvent(new ReportChangeEvent('reset'));
	}

	/** replace both snapshots (reload from server); unsaved changes are discarded */
	resetTo(context: ReportContext) {
		this._pristine = produce(context, draft => {
			ensureItemUids(draft.context as MultiContextData);
		});
		this._context = this._pristine;
		this.dispatchEvent(new ReportChangeEvent('reset'));
	}

	/** discard the draft, back to the pristine snapshot */
	discard() {
		if (!this.isDirty) return;
		this._context = this._pristine;
		this.dispatchEvent(new ReportChangeEvent('reset'));
	}

}
